/**
 * Consent screen for an AI assistant (an MCP client) asking to use Babia.
 * better-auth's OAuth provider redirects here with a signed query; the auth
 * client signs that query into the consent request, and the response's `url`
 * is where to send the browser next (back to the assistant, with a code or a
 * refusal). Shown outside the app shell so a parent without babies set up can
 * still answer it.
 */
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button.tsx";
import { track } from "../lib/analytics.ts";
import { ABILITIES, assistantName } from "../lib/assistants.ts";
import { authClient, useSession } from "../lib/auth-client.ts";

export function Consent() {
  const { data: session } = useSession();
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("client_id");
  const [clientName, setClientName] = useState<string | null>(null);
  const [pending, setPending] = useState<"allow" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    authClient.oauth2
      .publicClient({ query: { client_id: clientId } })
      .then(({ data }) => {
        if (!cancelled && data?.client_name) setClientName(data.client_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  async function answer(accept: boolean) {
    setError(null);
    setPending(accept ? "allow" : "deny");
    track("assistant consent answered", {
      accepted: accept,
      mcp_client_id: clientId,
      mcp_client_name: clientName,
    });
    const retry = "That didn't go through. Try again, or start again from your assistant.";
    try {
      const { data, error: failure } = await authClient.oauth2.consent({ accept });
      if (data?.url) {
        // Leave the buttons disabled while the browser navigates away.
        window.location.assign(data.url);
        return;
      }
      setError(failure?.message ?? retry);
    } catch {
      setError(retry);
    }
    setPending(null);
  }

  const name = assistantName({ name: clientName, clientId });

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col justify-center px-6 pb-16"
      style={{ paddingTop: "calc(24px + env(safe-area-inset-top))" }}
    >
      <h1 className="heading text-[26px] tracking-[-0.02em]">Babia</h1>
      <div className="head-rule" />
      {clientId ? (
        <>
          <h2 className="heading mt-6 text-[26px] leading-tight">{name} wants to use your log</h2>
          {session ? (
            <p className="mt-2 text-[14px] opacity-65">Signed in as {session.user.email}.</p>
          ) : null}
          <p className="mt-5 text-[15px]">If you allow it, it can:</p>
          <ul className="mt-2 flex flex-col gap-1.5 text-[15px]">
            {ABILITIES.map((ability) => (
              <li key={ability} className="border-t border-rule pt-1.5">
                {ability}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[14px] opacity-65">
            Entries it logs appear for everyone in your household, the same as a tap in the app. To
            answer you, it receives your babies’ names, birth dates, due dates, and log entries,
            including medicine names, doses, weights, and notes; its provider’s privacy policy
            covers your chats. See Babia’s{" "}
            {/* A new tab, so this signed consent request isn't lost. */}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener"
              className="underline underline-offset-4"
            >
              Privacy Policy
            </a>
            .
          </p>
          <div className="mt-8 flex flex-col gap-2">
            <Button variant="primary" disabled={pending !== null} onClick={() => answer(true)}>
              {pending === "allow" ? "Allowing…" : "Allow"}
            </Button>
            <Button variant="ghost" disabled={pending !== null} onClick={() => answer(false)}>
              {pending === "deny" ? "Declining…" : "Don't allow"}
            </Button>
          </div>
          {error ? <p className="mt-4 text-center text-[14px] text-accent-2">{error}</p> : null}
        </>
      ) : (
        <p className="mt-6 text-[15px] opacity-70">
          This link is incomplete. Start connecting again from your AI assistant.
        </p>
      )}
    </div>
  );
}
